import { createStore } from 'zustand/vanilla';
import type { SupportWidgetState, WidgetTab } from '../types';

/**
 * Factory that creates a namespaced Zustand vanilla store.
 * Persists `isOpen`, `activeTab`, `panelWidth`, and `seenReleases` to localStorage.
 */
export function createWidgetStore(productId: string, defaultWidth: number) {
  const storageKey = `${productId}-support-widget`;

  function loadPersistedState(): Partial<
    Pick<SupportWidgetState, 'activeTab' | 'panelWidth' | 'seenReleases'>
  > {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as {
        activeTab?: WidgetTab;
        panelWidth?: number;
        seenReleases?: string[];
      };
      return {
        activeTab: parsed.activeTab,
        panelWidth: parsed.panelWidth,
        seenReleases: parsed.seenReleases
          ? new Set(parsed.seenReleases)
          : undefined,
      };
    } catch {
      return {};
    }
  }

  function persist(state: SupportWidgetState) {
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          activeTab: state.activeTab,
          panelWidth: state.panelWidth,
          seenReleases: Array.from(state.seenReleases),
        }),
      );
    } catch {
      // localStorage may be full or unavailable — silently skip
    }
  }

  const persisted = loadPersistedState();

  return createStore<SupportWidgetState>()((set, get) => ({
    isOpen: false,
    activeTab: persisted.activeTab ?? 'tutorials',
    panelWidth: persisted.panelWidth ?? defaultWidth,
    seenReleases: persisted.seenReleases ?? new Set<string>(),

    toggle: () => {
      set((s) => ({ isOpen: !s.isOpen }));
    },
    open: () => set({ isOpen: true }),
    close: () => set({ isOpen: false }),
    setTab: (tab: WidgetTab) => {
      set({ activeTab: tab });
      persist(get());
    },
    setWidth: (width: number) => {
      set({ panelWidth: width });
      persist(get());
    },
    markReleasesSeen: (ids: string[]) => {
      const next = new Set(get().seenReleases);
      for (const id of ids) next.add(id);
      set({ seenReleases: next });
      persist({ ...get(), seenReleases: next });
    },
  }));
}

export type WidgetStore = ReturnType<typeof createWidgetStore>;
